import type { QueryKey } from '@tanstack/react-query';

import { placeHolder, type SwaggerResponse } from '@/services/config';
import { useWelfareController_allImages } from '@/services/hooks';
import type { FullSizeImage, WelfareController_allImagesQueryParams } from '@/services/types';

import { type DomainQuery, useMappedQuery } from '../shared/query';

import { type ImagesFishGroupScope, isScopeReady, withFishGroupScope } from './fishGroupScope';

export interface WelfareImagesOptions {
    enabled?: boolean;
    keepPreviousData?: boolean;
    /**
     * Start from an empty list instead of a loading state. The query then never reports
     * `isLoading`, so the page renders straight away and the images fill in.
     */
    emptyWhileLoading?: boolean;
    /**
     * The fish group the page is about (`usePenCohortFishGroup()`). Its id scopes the request,
     * which waits until the group is known and reports the lookup's failure as its own.
     */
    fishGroup?: ImagesFishGroupScope;
}

function rows(images: FullSizeImage[]): FullSizeImage[] {
    return images;
}

type ImagesResponse = SwaggerResponse<FullSizeImage[]>;

/** One empty list, so a page waiting for its fish group gets the same array on every render. */
const NO_IMAGES: FullSizeImage[] = [];

/** react-query's `placeholderData` function, over the images response. */
type ImagesPlaceholder = (
    previous: ImagesResponse | undefined,
    previousQuery?: { queryKey: QueryKey }
) => ImagesResponse | undefined;

/** The `fishGroupId` an images query asked for: the key is `[key, cageId, params]`. */
function fishGroupIdOf(queryKey: QueryKey): number | undefined {
    const params = queryKey[2];
    if (typeof params !== 'object' || params === null || !('fishGroupId' in params)) {
        return undefined;
    }
    return typeof params.fishGroupId === 'number' ? params.fishGroupId : undefined;
}

/**
 * A `placeholderData` that hands on the previous key's images while this one loads, but only
 * when both ask for the same fish group: another group's images under this group's name are the
 * wrong fish. Otherwise `instead` (the empty list, or no placeholder).
 */
export function previousImagesOfFishGroup(
    fishGroupId: number | undefined,
    instead: ImagesResponse | undefined
): ImagesPlaceholder {
    return (previous, previousQuery) =>
        previous !== undefined &&
        previousQuery !== undefined &&
        fishGroupIdOf(previousQuery.queryKey) === fishGroupId
            ? previous
            : instead;
}

/** react-query takes one `placeholderData`: the previous key's data wins over the empty list. */
function placeholderFor(
    fishGroupId: number | undefined,
    keepPrevious?: boolean,
    emptyWhileLoading?: boolean
) {
    const empty = emptyWhileLoading ? placeHolder<FullSizeImage[]>([]) : undefined;
    if (keepPrevious) return previousImagesOfFishGroup(fishGroupId, empty);
    return empty;
}

/**
 * The request params, with `fishGroupId` only when there is one, so a cage-wide request keeps the
 * query key it always had.
 */
export function welfareImagesParams(
    params: WelfareController_allImagesQueryParams
): WelfareController_allImagesQueryParams {
    const { fishGroupId, ...rest } = params;
    return fishGroupId != null ? { ...rest, fishGroupId } : rest;
}

/**
 * A pen's welfare or lice images of one type, newest first, as the API rows. With a fish group,
 * only those taken while that group lived in the pen.
 */
export function useGetWelfareImages(
    cageId: number,
    params: WelfareController_allImagesQueryParams,
    options: WelfareImagesOptions = {}
): DomainQuery<FullSizeImage[]> {
    const { emptyWhileLoading, keepPreviousData, fishGroup, ...queryOptions } = options;
    const requestParams = welfareImagesParams(
        fishGroup ? { ...params, fishGroupId: fishGroup.fishGroupId } : params
    );
    const placeholderData = placeholderFor(
        requestParams.fishGroupId,
        keepPreviousData,
        emptyWhileLoading
    );
    const images = useMappedQuery(
        useWelfareController_allImages(cageId, requestParams, {
            ...queryOptions,
            ...(fishGroup && {
                enabled: (queryOptions.enabled ?? true) && isScopeReady(fishGroup),
            }),
            ...(placeholderData && { placeholderData }),
        }),
        rows
    );
    return withFishGroupScope(images, fishGroup, emptyWhileLoading ? NO_IMAGES : undefined);
}
